import React, { Component, Fragment } from "react";
import {
  Card,
  Table,
  Button,
  Tag,
  Tooltip,
  Modal,
  notification,
  Select,
  Input,
  DatePicker,
  Switch,
  Row,
  Col,
  Space,
  Alert,
} from "antd";
import {
  SyncOutlined,
  SearchOutlined,
  ExclamationCircleOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  ThunderboltOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import moment from "moment";
import { connect } from "react-redux";
import Breadcrumb from "../common/breadcrumb";
import {
  fetchElectricityTransactionsAPI,
  recheckElectricityTransactionAPI,
  resolveElectricityTransactionAPI,
} from "../../Graphs/Report/electricityAPI";
import "./Electricity.scss";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

export class ElectricityTransactions extends Component {
  constructor(props) {
    super(props);
    this.state = {
      transactions: [],
      total: 0,
      page: 1,
      limit: 20,
      isLoading: false,

      // Filters
      startDate: moment().subtract(7, "days").format("YYYY-MM-DD"),
      endDate: moment().format("YYYY-MM-DD"),
      statusFilter: "",
      needsReviewOnly: false,
      searchQuery: "",

      // Actions state
      recheckingId: null,

      // Resolution Modal
      isResolveModalOpen: false,
      resolvingTx: null,
      resolutionType: "COMPLETE",
      stsTokenInput: "",
      providerRefInput: "",
      auditNoteInput: "",
      isResolving: false,
      modalError: "",
    };
  }

  componentDidMount() {
    this._isMounted = true;
    this.loadTransactions();
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  safeSetState = (state, callback) => {
    if (this._isMounted) {
      this.setState(state, callback);
    }
  };

  loadTransactions = async (pageOverride) => {
    try {
      this.safeSetState({ isLoading: true });
      const { authToken } = this.props;
      const {
        page,
        limit,
        startDate,
        endDate,
        statusFilter,
        needsReviewOnly,
        searchQuery,
      } = this.state;

      const currentPage = pageOverride !== undefined ? pageOverride : page;

      const params = {
        page: currentPage,
        limit,
      };

      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      if (statusFilter) params.status = statusFilter;
      if (needsReviewOnly) params.needs_review = "true";
      if (searchQuery && searchQuery.trim()) params.search = searchQuery.trim();

      const res = await fetchElectricityTransactionsAPI(authToken, params);

      const txList = (res && (res.transactions || res.data)) || [];
      if (Array.isArray(txList)) {
        this.safeSetState({
          transactions: txList,
          total: res.total || (res.meta && res.meta.total) || txList.length || 0,
          page: currentPage,
        });
      } else {
        this.safeSetState({ transactions: [], total: 0 });
      }
    } catch (err) {
      console.error("[ElectricityTransactions] loadTransactions error:", err);
      notification.error({
        message: "Failed to load transactions",
        description: err.message || "Network error",
      });
    } finally {
      this.safeSetState({ isLoading: false });
    }
  };

  handleFilterSubmit = () => {
    this.loadTransactions(1);
  };

  handleResetFilters = () => {
    this.safeSetState(
      {
        startDate: "",
        endDate: "",
        statusFilter: "",
        needsReviewOnly: false,
        searchQuery: "",
        page: 1,
      },
      () => {
        this.loadTransactions(1);
      }
    );
  };

  handleRecheckTransaction = async (record) => {
    try {
      this.safeSetState({ recheckingId: record.id });
      const { authToken } = this.props;
      const res = await recheckElectricityTransactionAPI(authToken, record.id);

      if (res && res.success) {
        notification.success({
          message: "Recheck Complete",
          description: res.message || "Provider status reconciled successfully.",
        });
        await this.loadTransactions();
      } else {
        notification.warning({
          message: "Recheck Pending",
          description: res?.message || "Order is still pending with provider.",
        });
        await this.loadTransactions();
      }
    } catch (err) {
      console.error("[ElectricityTransactions] handleRecheckTransaction error:", err);
      notification.error({
        message: "Recheck Failed",
        description: err.message || "Failed to contact upstream provider.",
      });
    } finally {
      this.safeSetState({ recheckingId: null });
    }
  };

  handleOpenResolveModal = (record) => {
    this.safeSetState({
      isResolveModalOpen: true,
      resolvingTx: record,
      resolutionType: "COMPLETE",
      stsTokenInput: "",
      providerRefInput: String(record.reference_id || record.upstream_history_id || ""),
      auditNoteInput: "",
      modalError: "",
    });
  };

  handleCloseResolveModal = () => {
    this.safeSetState({
      isResolveModalOpen: false,
      resolvingTx: null,
      stsTokenInput: "",
      providerRefInput: "",
      auditNoteInput: "",
      modalError: "",
    });
  };

  handleSubmitResolution = async () => {
    const {
      resolvingTx,
      resolutionType,
      stsTokenInput,
      providerRefInput,
      auditNoteInput,
    } = this.state;
    const { authToken } = this.props;

    if (!auditNoteInput || auditNoteInput.trim().length < 5) {
      this.safeSetState({
        modalError: "Audit note is mandatory (at least 5 characters) explaining the resolution reason.",
      });
      return;
    }

    let cleanToken = "";
    if (resolutionType === "COMPLETE") {
      cleanToken = stsTokenInput.replace(/[\s-]/g, "");
      if (!/^\d{20}$/.test(cleanToken)) {
        this.safeSetState({
          modalError: "STS Token must be exactly 20 digits (e.g. 1234 5678 9012 3456 7890).",
        });
        return;
      }
    }

    try {
      this.safeSetState({ isResolving: true, modalError: "" });
      const payload = {
        resolution: resolutionType,
        token: cleanToken,
        reference_id: providerRefInput.trim(),
        audit_note: auditNoteInput.trim(),
      };

      const res = await resolveElectricityTransactionAPI(authToken, resolvingTx.id, payload);

      if (res && res.success) {
        notification.success({
          message: "Transaction Resolved",
          description: res.message || "Manual resolution applied successfully.",
        });
        this.handleCloseResolveModal();
        await this.loadTransactions();
      } else {
        this.safeSetState({
          modalError: res?.message || "Failed to resolve transaction.",
        });
      }
    } catch (err) {
      console.error("[ElectricityTransactions] handleSubmitResolution error:", err);
      this.safeSetState({
        modalError: err.message || "An error occurred during resolution.",
      });
    } finally {
      this.safeSetState({ isResolving: false });
    }
  };

  formatStsToken = (token) => {
    if (!token) return "";
    const clean = token.replace(/[\s-]/g, "");
    if (clean.length !== 20) return token;
    return clean.match(/.{1,4}/g).join(" ");
  };

  copyToClipboard = (text, label) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text);
    }
    notification.success({
      message: "Copied",
      description: `${label || "Value"} copied to clipboard.`,
      duration: 2,
    });
  };

  render() {
    const {
      transactions,
      total,
      page,
      limit,
      isLoading,
      startDate,
      endDate,
      statusFilter,
      needsReviewOnly,
      searchQuery,
      recheckingId,
      isResolveModalOpen,
      resolvingTx,
      resolutionType,
      stsTokenInput,
      providerRefInput,
      auditNoteInput,
      isResolving,
      modalError,
    } = this.state;

    const completedCount = transactions.filter((t) => t.status === "COMPLETED").length;
    const pendingCount = transactions.filter((t) => t.status === "PENDING").length;
    const reviewCount = transactions.filter((t) => t.needs_review).length;

    const columns = [
      {
        title: "Order Code",
        dataIndex: "order_code",
        key: "order_code",
        width: 170,
        render: (code) => (
          <span style={{ fontWeight: 600 }}>
            {code}
            <Tooltip title="Copy Order Code">
              <Button
                type="link"
                size="small"
                icon={<CopyOutlined />}
                style={{ padding: "0 4px" }}
                onClick={() => this.copyToClipboard(code, "Order Code")}
              />
            </Tooltip>
          </span>
        ),
      },
      {
        title: "Date / Time",
        dataIndex: "createdAt",
        key: "createdAt",
        width: 150,
        render: (val) => (
          <div>
            <div>{moment(val).format("YYYY-MM-DD")}</div>
            <div style={{ fontSize: 11, color: "#8c8c8c" }}>{moment(val).format("HH:mm:ss")}</div>
          </div>
        ),
      },
      {
        title: "Merchant",
        key: "merchant",
        render: (_, record) => (
          <div>
            <div style={{ fontWeight: 600 }}>
              {record.user?.shop_name || `Merchant #${record.user_id}`}
            </div>
            {record.user?.phone && (
              <div style={{ fontSize: 12, color: "#8c8c8c" }}>{record.user.phone}</div>
            )}
          </div>
        ),
      },
      {
        title: "Meter & Customer",
        key: "meter",
        render: (_, record) => (
          <div>
            <div className="mono-text" style={{ fontWeight: 600 }}>
              {record.meter_number}
              <Tooltip title="Copy Meter Number">
                <Button
                  type="link"
                  size="small"
                  icon={<CopyOutlined />}
                  style={{ padding: "0 4px" }}
                  onClick={() => this.copyToClipboard(record.meter_number, "Meter Number")}
                />
              </Tooltip>
            </div>
            <div style={{ fontSize: 12, color: "#595959" }}>{record.customer_phone}</div>
          </div>
        ),
      },
      {
        title: "Denom / Sell",
        key: "amounts",
        align: "right",
        render: (_, record) => {
          const denom =
            record.denomination ||
            record.package?.denomination ||
            (record.sell_price ? Math.round(record.sell_price) : 0);
          return (
            <div>
              <div>{Number(denom || 0).toFixed(0)} LYD</div>
              <div style={{ fontWeight: 600, color: "#1890ff", fontFamily: "monospace" }}>
                {Number(record.sell_price || 0).toFixed(3)} LYD
              </div>
            </div>
          );
        },
      },
      {
        title: "Cost / Profit",
        key: "profit",
        align: "right",
        render: (_, record) => (
          <div>
            <div style={{ color: "#8c8c8c", fontFamily: "monospace", fontSize: 12 }}>
              Cost: {Number(record.cost_price || 0).toFixed(3)}
            </div>
            <div style={{ color: "#52c41a", fontFamily: "monospace", fontWeight: 600 }}>
              +{Number(record.profit || 0).toFixed(3)} LYD
            </div>
          </div>
        ),
      },
      {
        title: "STS Recharge Token",
        key: "token",
        width: 220,
        render: (_, record) => {
          if (record.sts_token) {
            const formatted = this.formatStsToken(record.sts_token);
            return (
              <span className="sts-token-badge">
                <span>{formatted}</span>
                <Tooltip title="Copy STS Token">
                  <CopyOutlined
                    style={{ cursor: "pointer" }}
                    onClick={() => this.copyToClipboard(record.sts_token, "STS Token")}
                  />
                </Tooltip>
              </span>
            );
          }
          return <span style={{ color: "#bfbfbf" }}>—</span>;
        },
      },
      {
        title: "Status",
        key: "status",
        align: "center",
        render: (_, record) => {
          let statusTag;
          if (record.status === "COMPLETED") {
            statusTag = <Tag color="success">COMPLETED</Tag>;
          } else if (record.status === "PENDING") {
            statusTag = <Tag color="warning">PENDING</Tag>;
          } else if (record.status === "REFUNDED") {
            statusTag = <Tag color="purple">REFUNDED</Tag>;
          } else {
            statusTag = <Tag>{record.status}</Tag>;
          }

          return (
            <Space orientation="vertical" size={2}>
              {statusTag}
              {record.needs_review && (
                <Tag color="error" icon={<ExclamationCircleOutlined />}>
                  Needs Review
                </Tag>
              )}
            </Space>
          );
        },
      },
      {
        title: "Provider Ref",
        key: "provider_ref",
        render: (_, record) => (
          <div>
            {record.upstream_history_id && (
              <div style={{ fontSize: 12 }}>
                Hist: <span className="mono-text">#{record.upstream_history_id}</span>
              </div>
            )}
            {record.reference_id && (
              <div style={{ fontSize: 11, color: "#8c8c8c" }}>Ref: {record.reference_id}</div>
            )}
            {record.audit_note && (
              <Tooltip title={`Audit Note: ${record.audit_note}`}>
                <Tag color="blue" icon={<InfoCircleOutlined />} style={{ cursor: "pointer", marginTop: 2 }}>
                  Audited
                </Tag>
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        title: "Actions",
        key: "actions",
        align: "center",
        width: 140,
        render: (_, record) => (
          <Space size={4}>
            {record.status === "PENDING" && record.upstream_history_id && (
              <Tooltip title="Query Provider for latest outcome">
                <Button
                  size="small"
                  icon={<SyncOutlined spin={recheckingId === record.id} />}
                  loading={recheckingId === record.id}
                  onClick={() => this.handleRecheckTransaction(record)}
                >
                  Recheck
                </Button>
              </Tooltip>
            )}
            {(record.status === "PENDING" || record.needs_review) && (
              <Tooltip title="Manually Complete">
                <Button
                  size="small"
                  type="primary"
                  danger={record.needs_review}
                  icon={<ExclamationCircleOutlined />}
                  onClick={() => this.handleOpenResolveModal(record)}
                >
                  Resolve
                </Button>
              </Tooltip>
            )}
          </Space>
        ),
      },
    ];

    return (
      <Fragment>
        <Breadcrumb title="Electricity Transactions" parent="Electricity" child="Transactions" url="/dashboard" />
        <div className="container-fluid electricity-page">
          {/* Header Card */}
          <Card className="electricity-header-card" bordered={false}>
            <div className="header-content">
              <div className="title-section">
                <h4>
                  <ThunderboltOutlined style={{ color: "#faad14" }} />
                  Electricity Recharge Transactions Ledger
                </h4>
                <p>
                  Audit merchant recharge orders, STS tokens, provider reconciliation status, and resolve exceptions.
                </p>
              </div>
              <div className="header-actions">
                <Button
                  type="primary"
                  icon={<SyncOutlined />}
                  onClick={() => this.loadTransactions()}
                  loading={isLoading}
                >
                  Refresh Ledger
                </Button>
              </div>
            </div>
          </Card>

          {/* KPI Summary Cards */}
          <div className="kpi-row">
            <div className="kpi-card">
              <div className="kpi-icon-wrap primary">
                <ThunderboltOutlined />
              </div>
              <div className="kpi-info">
                <div className="kpi-label">Total Transactions</div>
                <div className="kpi-value">{total}</div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon-wrap success">
                <CheckCircleOutlined />
              </div>
              <div className="kpi-info">
                <div className="kpi-label">Completed (Page)</div>
                <div className="kpi-value">{completedCount}</div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon-wrap warning">
                <ClockCircleOutlined />
              </div>
              <div className="kpi-info">
                <div className="kpi-label">Pending (Page)</div>
                <div className="kpi-value">{pendingCount}</div>
              </div>
            </div>

            <div className="kpi-card">
              <div className="kpi-icon-wrap danger">
                <ExclamationCircleOutlined />
              </div>
              <div className="kpi-info">
                <div className="kpi-label">Needs Review (Page)</div>
                <div className="kpi-value" style={{ color: reviewCount > 0 ? "#ff4d4f" : "#262626" }}>
                  {reviewCount}
                </div>
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <Card className="filter-card" bordered={false}>
            <Row gutter={[16, 12]} align="middle">
              <Col xs={24} sm={12} md={6}>
                <RangePicker
                  style={{ width: "100%" }}
                  value={
                    startDate && endDate
                      ? [moment(startDate, "YYYY-MM-DD"), moment(endDate, "YYYY-MM-DD")]
                      : []
                  }
                  onChange={(dates, dateStrings) => {
                    this.safeSetState({
                      startDate: dateStrings[0] || "",
                      endDate: dateStrings[1] || "",
                    });
                  }}
                />
              </Col>

              <Col xs={24} sm={12} md={4}>
                <Select
                  style={{ width: "100%" }}
                  placeholder="All Statuses"
                  value={statusFilter || undefined}
                  onChange={(val) => this.safeSetState({ statusFilter: val || "" })}
                  allowClear
                >
                  <Option value="COMPLETED">Completed</Option>
                  <Option value="PENDING">Pending</Option>
                  <Option value="REFUNDED">Refunded</Option>
                </Select>
              </Col>

              <Col xs={24} sm={12} md={6}>
                <Input
                  placeholder="Order Code, Meter #, Phone..."
                  prefix={<SearchOutlined style={{ color: "#bfbfbf" }} />}
                  value={searchQuery}
                  onChange={(e) => this.safeSetState({ searchQuery: e.target.value })}
                  onPressEnter={this.handleFilterSubmit}
                  allowClear
                />
              </Col>

              <Col xs={24} sm={12} md={4}>
                <Space align="center">
                  <Switch
                    checked={needsReviewOnly}
                    onChange={(checked) => this.safeSetState({ needsReviewOnly: checked })}
                  />
                  <span style={{ fontSize: 13, fontWeight: 500 }}>Needs Review Only</span>
                </Space>
              </Col>

              <Col xs={24} sm={24} md={4} style={{ textAlign: "right" }}>
                <Space>
                  <Button type="primary" icon={<SearchOutlined />} onClick={this.handleFilterSubmit}>
                    Filter
                  </Button>
                  <Button onClick={this.handleResetFilters}>Reset</Button>
                </Space>
              </Col>
            </Row>
          </Card>

          {/* Transactions Table */}
          <Card className="table-card" bordered={false}>
            <Table
              dataSource={transactions}
              columns={columns}
              rowKey="id"
              loading={isLoading}
              pagination={{
                current: page,
                pageSize: limit,
                total,
                showSizeChanger: false,
                onChange: (p) => this.loadTransactions(p),
                showTotal: (totalCount) => `Total ${totalCount} transactions`,
              }}
              bordered
              scroll={{ x: 1200 }}
            />
          </Card>

          {/* Manual Resolution Modal */}
          <Modal
            title={
              <span>
                <ExclamationCircleOutlined style={{ color: "#faad14", marginRight: 8 }} />
                Resolve Order: {resolvingTx?.order_code}
              </span>
            }
            visible={isResolveModalOpen}
            open={isResolveModalOpen}
            onCancel={this.handleCloseResolveModal}
            footer={[
              <Button key="cancel" onClick={this.handleCloseResolveModal}>
                Cancel
              </Button>,
              <Button
                key="submit"
                type="primary"
                loading={isResolving}
                onClick={this.handleSubmitResolution}
              >
                Confirm Completion
              </Button>,
            ]}
            destroyOnClose
            width={600}
          >
            {modalError && (
              <Alert
                type="error"
                message={modalError}
                showIcon
                style={{ marginBottom: 16 }}
              />
            )}

            {resolvingTx && (
              <div className="order-summary-box">
                <div className="summary-row">
                  <span className="summary-label">Order Code:</span>
                  <span className="summary-value">{resolvingTx.order_code}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Merchant Shop:</span>
                  <span className="summary-value">
                    {resolvingTx.user?.shop_name || `Merchant #${resolvingTx.user_id}`}
                  </span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Meter Number:</span>
                  <span className="summary-value mono-text">{resolvingTx.meter_number}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Customer Phone:</span>
                  <span className="summary-value">{resolvingTx.customer_phone}</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Sell Amount (Debited):</span>
                  <span className="summary-value" style={{ color: "#1890ff" }}>
                    {Number(resolvingTx.sell_price || 0).toFixed(3)} LYD
                  </span>
                </div>
                {resolvingTx.upstream_history_id && (
                  <div className="summary-row">
                    <span className="summary-label">Upstream History ID:</span>
                    <span className="summary-value mono-text">#{resolvingTx.upstream_history_id}</span>
                  </div>
                )}
              </div>
            )}

            <Alert
              type="info"
              message="Completion Policy"
              description="Order will be marked COMPLETED, the STS token saved, and admin profit recognized. Ensure the token has been verified with GECOL / Khales provider support."
              showIcon
              style={{ marginBottom: 16 }}
            />
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
                20-digit STS Token (Required):
              </label>
              <Input
                placeholder="e.g. 1234 5678 9012 3456 7890"
                value={stsTokenInput}
                onChange={(e) => this.safeSetState({ stsTokenInput: e.target.value })}
                maxLength={25}
                style={{ fontFamily: "monospace", letterSpacing: 1 }}
              />
              <span style={{ fontSize: 12, color: "#8c8c8c" }}>
                Whitespace and dashes will be stripped automatically. Exactly 20 digits required.
              </span>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
                Provider Reference / Ticket # (Optional):
              </label>
              <Input
                placeholder="e.g. Khales support ticket #54321"
                value={providerRefInput}
                onChange={(e) => this.safeSetState({ providerRefInput: e.target.value })}
              />
            </div>

            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 6 }}>
                Audit Note (Mandatory):
              </label>
              <TextArea
                rows={3}
                placeholder="Explain the reason for this manual resolution (e.g. 'Confirmed with Khales operator Ali that meter failed at substation')."
                value={auditNoteInput}
                onChange={(e) => this.safeSetState({ auditNoteInput: e.target.value })}
              />
            </div>
          </Modal>
        </div>
      </Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  authToken:
    state.auth?.authToken ||
    state.auth?.token ||
    state.auth?.admin_token ||
    "",
  loginUserData: state.auth?.loginUserData,
});

export default connect(mapStateToProps)(ElectricityTransactions);
